<template>
    <section class="platform-telegram" aria-labelledby="telegram-connection-title">
        <div class="platform-telegram__heading">
            <div>
                <h2 id="telegram-connection-title">Подключение Telegram</h2>
                <p>Аккаунт, от имени которого Аргус создаёт группы объектов обеих компаний.</p>
            </div>
            <b-badge :variant="statusVariant">{{ statusLabel }}</b-badge>
        </div>
        <b-alert :show="!!error" variant="danger">{{ error }}</b-alert>
        <template v-if="status">
            <p role="status">{{ status.message }}</p>
            <dl class="platform-telegram__facts">
                <template v-if="status.account || status.expectedAccountId">
                    <dt>Аккаунт</dt>
                    <dd>{{ accountLabel }} <span class="text-muted">· ID {{ status.expectedAccountId }}</span></dd>
                </template>
                <dt>Последняя проверка</dt><dd>{{ formatDate(status.lastCheckAt) }}</dd>
                <dt>Последнее подключение</dt><dd>{{ formatDate(status.lastSuccessAt) }}</dd>
                <template v-if="status.retryAt"><dt>Повтор после</dt><dd>{{ formatDate(status.retryAt) }}</dd></template>
            </dl>
            <div v-if="status.enabled" class="platform-telegram__actions">
                <b-button variant="outline-secondary" :disabled="busy" @click="check">{{ busy ? 'Проверка…' : 'Проверить подключение' }}</b-button>
                <b-button variant="success" :disabled="busy || otherAttempt" @click="openLogin">
                    {{ ownAttempt ? 'Продолжить вход' : status.state === 'connected' ? 'Войти заново' : 'Восстановить доступ' }}
                </b-button>
            </div>
            <p v-if="otherAttempt" class="text-muted mt-3 mb-0">Другой администратор выполняет вход. Попытка действует до {{ formatDate(status.attempt.expiresAt) }}.</p>
            <p v-if="status.enabled" class="platform-telegram__note">При потере доступа мониторинг создаст инцидент и отправит уведомление через бота Аргуса. После входа безопасно отложенные задания продолжатся автоматически.</p>
        </template>
        <p v-else-if="busy">Загрузка состояния…</p>

        <b-modal v-model="showLogin" :title="candidateOperation ? 'Подключение нового рабочего аккаунта' : 'Восстановление Telegram'" @shown="focusInput" @hidden="clearInput">
            <div class="platform-telegram-login">
                <p v-if="candidateOperation">Подключите аккаунт <strong>{{ candidateOperation.accountId }}</strong> как кандидата. Рабочая сессия сменится только после отдельной проверки и активации.</p>
                <p v-else>Войдите в прежний аккаунт <strong>{{ accountLabel }}</strong> <span v-if="status && status.expectedAccountId">(ID {{ status.expectedAccountId }})</span>.</p>
                <template v-if="!attempt">
                    <p class="text-muted">Откройте Telegram на телефоне и подтвердите вход по QR-коду. Если удобнее, используйте номер телефона.</p>
                    <div class="platform-telegram__actions">
                        <b-button variant="success" :disabled="loginBusy" @click="begin('qr')">Войти по QR-коду</b-button>
                        <b-button variant="outline-secondary" :disabled="loginBusy" @click="begin('phone')">По номеру телефона</b-button>
                    </div>
                </template>
                <template v-else>
                    <b-alert :show="!!attempt.message" :variant="attempt.stage === 'completed' ? 'success' : terminal ? 'warning' : 'info'" aria-live="polite">{{ attempt.message }}</b-alert>
                    <div v-if="attempt.stage === 'qr'" class="platform-telegram-login__qr">
                        <p v-if="attempt.confirming">Telegram подтвердил QR-код. Завершаем подключение; повторно сканировать не нужно.</p>
                        <p v-else>Telegram → Настройки → Устройства → Подключить устройство. Отсканируйте QR-код.</p>
                        <img v-if="validQr" :src="attempt.qrImage" width="260" height="260" alt="QR-код для входа в Telegram" draggable="false">
                        <div v-else class="platform-telegram-login__qr-placeholder" role="status">{{ attempt.confirming ? 'Завершаем вход…' : loginBusy ? 'Получаем QR-код…' : 'Обновляем QR-код…' }}</div>
                        <small v-if="!attempt.confirming" class="text-muted d-block">QR-код обновляется автоматически. На одном телефоне можно выбрать вход по номеру.</small>
                    </div>
                    <form v-if="inputStage" @submit.prevent="submitStep">
                        <b-form-group :label="inputLabel" label-for="telegram-login-input" :description="inputDescription">
                            <b-form-input id="telegram-login-input" ref="loginInput" :key="attempt.stage" v-model="inputValue"
                                :type="attempt.stage === 'password' ? 'password' : attempt.stage === 'phone' ? 'tel' : 'text'"
                                :inputmode="attempt.stage === 'code' ? 'numeric' : attempt.stage === 'phone' ? 'tel' : 'text'"
                                :maxlength="attempt.stage === 'password' ? 256 : attempt.stage === 'phone' ? 16 : 8"
                                autocomplete="off" :disabled="loginBusy || cooldown" required/>
                        </b-form-group>
                        <b-button type="submit" variant="success" :disabled="loginBusy || cooldown || !inputValue.trim()">{{ loginBusy ? 'Проверяем…' : attempt.stage === 'phone' ? 'Получить код' : 'Подтвердить' }}</b-button>
                        <b-button v-if="attempt.stage === 'code' && attempt.canResend" variant="link" :disabled="loginBusy || cooldown || resendWait > 0" @click="sendStep('resend')">
                            {{ resendWait > 0 ? `Повторить через ${resendWait} с` : 'Отправить код ещё раз' }}
                        </b-button>
                    </form>
                    <p v-if="cooldown" class="text-muted mt-3">Следующая попытка после {{ formatDate(attempt.retryAt) }}.</p>
                    <p v-if="!terminal" class="text-muted small mt-3">Попытка действует до {{ formatDate(attempt.expiresAt) }}. Код и пароль не сохраняются. Закрыв окно, можно вернуться и продолжить вход.</p>
                    <div class="platform-telegram__actions mt-3">
                        <b-button v-if="!terminal" variant="outline-secondary" :disabled="loginBusy" @click="cancel">Отменить вход</b-button>
                        <b-button v-else-if="attempt.stage !== 'completed'" variant="outline-secondary" :disabled="loginBusy || cooldown" @click="attempt = null">Начать заново</b-button>
                        <b-button v-if="attempt.stage === 'completed'" variant="success" @click="showLogin = false">Готово</b-button>
                    </div>
                </template>
            </div>
            <template #modal-footer>
                <div class="w-100"><p v-if="loginError" class="alert alert-danger" role="alert">{{ loginError }}</p>
                    <b-button variant="outline-secondary" @click="showLogin = false">Закрыть</b-button></div>
            </template>
        </b-modal>
    </section>
</template>

<script>
import platformApi from '@/platform/api';
import {newId} from '@/utils/objectCreationDraft';

const URL = '/integrations/telegram';
const TERMINAL = ['completed', 'failed', 'cancelled', 'expired'];
const LOGIN_REQUEST = {timeout: 60000};
export default {
    name: 'PlatformTelegramConnection',
    data() {
        return {status: null, busy: false, error: '', showLogin: false, attempt: null, loginBusy: false,
            loginError: '', inputValue: '', candidateOperation: null, now: Date.now(), pollTimer: null, clockTimer: null, disposed: false};
    },
    computed: {
        statusLabel() { return this.status ? ({connected: 'Подключён', auth_required: 'Нужен вход', unavailable: 'Нет соединения', rate_limited: 'Ограничение Telegram', configuration_error: 'Нужна настройка', disabled: 'Отключён на стенде', unchecked: 'Не проверен', busy: 'Выполняется операция'}[this.status.state] || 'Не проверен') : 'Загрузка…'; },
        statusVariant() { return !this.status ? 'secondary' : this.status.state === 'connected' ? 'success' : ['disabled', 'unchecked', 'busy'].includes(this.status.state) ? 'secondary' : 'warning'; },
        accountLabel() { const account = this.status && this.status.account; return account ? [account.name, account.username ? '@' + account.username : account.phone].filter(Boolean).join(' · ') : 'создания групп'; },
        activeAttempt() { const value = this.status && this.status.attempt; return value && !TERMINAL.includes(value.stage) && Date.parse(value.expiresAt) > this.now ? value : null; },
        ownAttempt() { return this.activeAttempt && this.activeAttempt.mine; },
        otherAttempt() { return this.activeAttempt && !this.activeAttempt.mine; },
        terminal() { return this.attempt && TERMINAL.includes(this.attempt.stage); },
        inputStage() { return this.attempt && ['phone', 'code', 'password'].includes(this.attempt.stage); },
        inputLabel() { return {phone: 'Номер телефона', code: 'Код подтверждения', password: 'Пароль двухэтапной аутентификации'}[this.attempt.stage]; },
        inputDescription() { return this.attempt.stage === 'phone' ? 'В международном формате, например +375291234567.' : this.attempt.stage === 'code' ? `Код придёт через ${this.attempt.delivery || 'Telegram'}.` : 'Пароль аккаунта Telegram, который вы задали в его настройках.'; },
        validQr() { return this.attempt && this.attempt.qrImage && Date.parse(this.attempt.qrExpiresAt) > this.now; },
        cooldown() { return this.attempt && Date.parse(this.attempt.retryAt) > this.now; },
        resendWait() { return this.attempt ? Math.max(0, Math.ceil((Date.parse(this.attempt.resendAt) - this.now) / 1000)) || 0 : 0; }
    },
    created() {
        this.loadStatus();
        this.clockTimer = setInterval(() => { this.now = Date.now(); }, 1000);
        this.schedulePoll();
    },
    beforeDestroy() {
        this.disposed = true; clearTimeout(this.pollTimer); clearInterval(this.clockTimer); this.clearInput(); this.attempt = null;
    },
    methods: {
        message(error) {
            const data = error.response && error.response.data;
            if (data && data.code === 'TG_BUSY') return 'Предыдущая операция Telegram ещё завершается. Состояние обновится автоматически; повторно сканировать QR-код не нужно.';
            return data && data.msg || 'Нет ответа сервера. Обновите состояние; повторный вход мог уже завершиться.';
        },
        formatDate(value) { return value ? new Date(value).toLocaleString('ru-RU') : 'Ещё не было'; },
        clearInput() { this.inputValue = ''; this.loginError = ''; },
        focusInput() { this.$nextTick(() => { if (!this.disposed && this.$refs.loginInput) this.$refs.loginInput.focus(); }); },
        async loadStatus() {
            if (this.busy || this.disposed) return;
            this.busy = true;
            try { const {data} = await platformApi.get(URL); if (!this.disposed) { this.status = data; this.error = ''; } }
            catch (error) { if (!this.disposed) this.error = this.message(error); }
            finally { this.busy = false; }
        },
        async check() {
            if (this.busy) return;
            this.busy = true; this.error = '';
            try { const {data} = await platformApi.post(URL + '/check'); if (!this.disposed) this.status = data; }
            catch (error) { if (!this.disposed) this.error = this.message(error); }
            finally { this.busy = false; }
        },
        async openLogin() {
            this.candidateOperation = null;
            this.clearInput(); this.attempt = null; this.showLogin = true;
            clearTimeout(this.pollTimer); this.schedulePoll();
            if (this.ownAttempt) await this.pollAttempt(this.activeAttempt.id);
        },
        async openCandidate(op) {
            await this.openLogin();
            this.candidateOperation = {operationId: op.id, accountId: op.params.accounts.newOwner.id};
        },
        accept(data) {
            if (this.disposed) return;
            const oldStage = this.attempt && this.attempt.stage;
            this.attempt = data; if (data.state) this.status = data.state;
            if (data.replacement) this.candidateOperation = data.replacement;
            if (data.stage === 'completed') this.$emit('changed');
            if (oldStage !== data.stage) { this.inputValue = ''; this.focusInput(); }
        },
        async begin(method) {
            if (this.loginBusy) return;
            this.loginBusy = true; this.loginError = '';
            try { this.accept((await platformApi.post(this.candidateOperation ? `${URL}/operations/${this.candidateOperation.operationId}/candidate` : URL + '/reauthorization', {method}, LOGIN_REQUEST)).data); }
            catch (error) { if (!this.disposed) this.loginError = this.message(error); }
            finally { this.loginBusy = false; }
        },
        async pollAttempt(id) {
            if (this.loginBusy) return;
            this.loginBusy = true;
            try { this.accept((await platformApi.get(`${URL}/reauthorization/${id}`, LOGIN_REQUEST)).data); this.loginError = ''; }
            catch (error) { if (!this.disposed) this.loginError = this.message(error); }
            finally { this.loginBusy = false; }
        },
        submitStep() { return this.sendStep(this.attempt.stage, this.inputValue); },
        async sendStep(action, value) {
            if (this.loginBusy) return;
            this.loginBusy = true; this.loginError = ''; this.inputValue = '';
            try { this.accept((await platformApi.post(`${URL}/reauthorization/${this.attempt.id}/step`, {action, value, requestId: newId()}, LOGIN_REQUEST)).data); }
            catch (error) { if (!this.disposed) this.loginError = this.message(error); }
            finally { this.loginBusy = false; this.focusInput(); }
        },
        async cancel() {
            if (this.loginBusy) return;
            this.loginBusy = true; this.clearInput();
            try { this.accept((await platformApi.delete(`${URL}/reauthorization/${this.attempt.id}`, LOGIN_REQUEST)).data); }
            catch (error) { if (!this.disposed) this.loginError = this.message(error); }
            finally { this.loginBusy = false; }
        },
        schedulePoll() {
            this.pollTimer = setTimeout(async () => {
                if (this.disposed) return;
                if (!document.hidden) {
                    if (this.showLogin && this.attempt && !this.terminal) await this.pollAttempt(this.attempt.id);
                    else if (!this.showLogin) await this.loadStatus();
                }
                if (!this.disposed) this.schedulePoll();
            }, this.showLogin ? 3000 : 15000);
        }
    }
};
</script>

<style scoped>
.platform-telegram { padding: 20px; border: 1px solid #dce3e5; border-radius: 11px; background: #fff; }
.platform-telegram__heading { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-bottom: 16px; }
.platform-telegram h2 { font-size: 20px; margin: 0 0 6px; }
.platform-telegram__heading p, .platform-telegram__note { color: #60747b; margin-bottom: 0; }
.platform-telegram__heading .badge { white-space: normal; }
.platform-telegram__facts { display: grid; grid-template-columns: max-content minmax(0, 1fr); gap: 8px 20px; font-size: 14px; }
.platform-telegram__facts dt { color: #60747b; font-weight: 400; }
.platform-telegram__facts dd { margin: 0; overflow-wrap: anywhere; }
.platform-telegram__actions { display: flex; gap: 8px; flex-wrap: wrap; }
.platform-telegram__note { margin-top: 16px; font-size: 13px; max-width: 780px; }
.platform-telegram-login__qr { text-align: center; }
.platform-telegram-login__qr img { max-width: 100%; height: auto; }
.platform-telegram-login__qr-placeholder { width: 260px; max-width: 100%; height: 260px; margin: auto; display: flex; align-items: center; justify-content: center; background: #f5f7f8; }
.platform-telegram-login input { font-size: 16px; }
@media (max-width: 575px) {
    .platform-telegram { padding: 16px; }
    .platform-telegram__heading { flex-direction: column; gap: 8px; }
    .platform-telegram__facts { grid-template-columns: 1fr; gap: 3px; }
    .platform-telegram__facts dd { margin-bottom: 8px; }
}
</style>
